import { Button } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useDialog } from "lib/@hooks/useDialog"
import { errorSnackbar } from "lib/snackbar/error-snackbar"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { warningSnackbar } from "lib/snackbar/warning-snackbar"
import Iconify from "minimals-template/components/Iconify"
import { Secure } from "lib/authorization/secure"
import { Frag } from "lib/@components/slot/frag"
import { ProjectPhase } from "authoring/lib/enum/project-phase.mjs"
import { AUTHORING_MANAGE_PUBLISHED_DATES_DEMAND } from "authoring/lib/constants/authoring-permissions.mjs"
import { setPublishedDate } from "../controller/set-published-date"
import { getDraftOrPublishedSchedules } from "../controller/get-draft-or-published-schedules"
import { isSendToLegacyBlockingSchedule } from "../lib/is-send-to-legacy-blocking-schedule"
import { attachPublishedSourceData } from "../lib/attach-published-source-data"
import { SetPublishedDateDialog } from "../components/set-published-date-dialog"

export function SetPublishedDateButton() {
    const { target: project } = useBoundContext()
    const scheduleIds = project?.scopedScheduleIds ?? []
    const publishedDateDialog = useDialog(<SetPublishedDateDialog />, { maxWidth: "md" })

    if (project?.phase !== ProjectPhase.PUSHED_TO_LEGACY) return null
    if (project?.archived) return null

    return (
        <Secure demand={AUTHORING_MANAGE_PUBLISHED_DATES_DEMAND} fallback={<Frag />}>
            <Button
                data-cy="set-published-date-button"
                startIcon={<Iconify icon={"material-symbols:event-available-outline"} />}
                variant="outlined"
                onClick={handleClick}
            >
                Set published date
            </Button>
        </Secure>
    )

    async function handleClick() {
        const approvedSchedules = await getApprovedSchedules()
        if (!approvedSchedules) {
            errorSnackbar("Could not load the approved schedules. Please try again.")
            return
        }

        const publishDate = await publishedDateDialog({ approvedSchedules })
        if (!publishDate) return

        const result = await setPublishedDate({ projectId: project?.id, publishDate })
        if (!result?.success) {
            errorSnackbar(result?.error || "Failed to set published date")
            return
        }

        const warnings = result?.warnings ?? []
        if (warnings.length) {
            warningSnackbar("Published date set, but some schedules could not be updated")
            for (const warning of warnings) {
                warningSnackbar(warning)
            }
            return
        }

        successSnackbar("Published date set")
    }

    async function getApprovedSchedules() {
        try {
            const schedules = await getDraftOrPublishedSchedules({ projectId: project?.id, scheduleIds })
            const resolvedSchedules = Array.isArray(schedules) ? schedules : []
            const approved = resolvedSchedules.filter((schedule) => !isSendToLegacyBlockingSchedule(schedule))
            return attachPublishedSourceData({ projectId: project?.id, schedules: approved })
        } catch (error) {
            console.error(error)
            return null
        }
    }
}
